// src/components/blog/admin/BlogPostEditor.js
import React, { useState, useEffect, useRef, useContext } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { db, storage } from '../../../firebase';
import { 
  doc, 
  getDoc, 
  setDoc, 
  updateDoc, 
  serverTimestamp, 
  Timestamp,
  collection
} from 'firebase/firestore';
import { ref, uploadBytes, getDownloadURL } from 'firebase/storage';
import { toast } from 'react-toastify';
import { ArrowLeft, Save, Eye, Upload, Image, X } from 'lucide-react';
import LoadingSpinner from '../LoadingSpinner';
import { AuthContext } from '../../../contexts/AuthContext';

function BlogPostEditor({ onSaved }) {
  const { postId } = useParams();
  const navigate = useNavigate();
  const isEditing = Boolean(postId);
  const [csrfToken, setCsrfToken] = useState('');
  const { currentUser, userRole } = useContext(AuthContext);
  
  // Helper function to generate placeholder image URLs
  const generatePlaceholderImage = (topic) => {
    const encodedTopic = encodeURIComponent(topic || 'Video Cover Letter Tips');
    return `https://placehold.co/1280x720?text=${encodedTopic}`;
  };

  // Form state
  const sampleMarkdown = `# Main Heading

## Introduction

This is a paragraph of text. Markdown supports *italic*, **bold**, and even ***bold italic*** text.

## Key Points

Here are some key points:

1. First important point
2. Second important point
3. Third important point

## Adding Lists

- Bullet point 1
- Bullet point 2
- Bullet point 3

## Adding Links and Images

Visit [ReelIntro](https://reelintro.com) for more information.

![Image description](https://placehold.co/800x400?text=Example+Image)

## Quotes

> This is a blockquote. It's great for highlighting important information or testimonials.

## Code Examples

For inline code, use \`code like this\`.

For code blocks:

\`\`\`javascript
function greet() {
  console.log("Hello, world!");
}
\`\`\`

## Conclusion

Thanks for reading!
`;

  const [formData, setFormData] = useState({
    title: '',
    content: '',
    excerpt: '',
    imageUrl: '',
    category: '',
    status: 'draft',
    slug: '',
    publishDate: new Date().toISOString().split('T')[0]
  });
  
  const [loading, setLoading] = useState(false);
  const [loadingPost, setLoadingPost] = useState(isEditing);
  const [previewMode, setPreviewMode] = useState(false);
  const [uploadingImage, setUploadingImage] = useState(false);
  const [imageFile, setImageFile] = useState(null);
  const [imagePreview, setImagePreview] = useState(null);
  const fileInputRef = useRef(null);
  
  // Generate CSRF token and load post data if editing
  useEffect(() => {
    // Generate a CSRF token for form protection
    const generateCsrfToken = () => {
      const token = Math.random().toString(36).substring(2, 15) + 
                   Math.random().toString(36).substring(2, 15);
      setCsrfToken(token);
      
      // In a real application, you would store this token in a cookie or localStorage
      // and validate it on the server side
      sessionStorage.setItem('blog_editor_csrf_token', token);
    };

    generateCsrfToken();
    
    const fetchPost = async () => {
      if (!postId) return;
      
      try {
        setLoadingPost(true);
        const postRef = doc(db, 'blog_posts', postId);
        const postSnap = await getDoc(postRef);
        
        if (postSnap.exists()) {
          const postData = postSnap.data();
          setFormData({
            title: postData.title || '',
            content: postData.content || '',
            excerpt: postData.excerpt || '',
            imageUrl: postData.imageUrl || '',
            category: postData.category || '',
            status: postData.status || 'draft',
            slug: postData.slug || '',
            publishDate: postData.publishDate 
              ? postData.publishDate.toDate().toISOString().split('T')[0]
              : new Date().toISOString().split('T')[0]
          });
        } else {
          toast.error('Post not found');
          navigate('/blog/admin');
        }
      } catch (err) {
        console.error('Error loading post:', err);
        toast.error('Failed to load post');
      } finally {
        setLoadingPost(false);
      }
    };
    
    fetchPost();
  }, [postId, navigate]);
  
  // Generate a slug from a string
  const generateSlug = (text) => {
    return text
      .toLowerCase()
      .replace(/[^\w\s-]/g, '') // Remove special characters
      .replace(/\s+/g, '-')     // Replace spaces with hyphens
      .replace(/-+/g, '-')      // Replace multiple hyphens with a single hyphen
      .trim();                   // Trim leading/trailing spaces or hyphens
  };
  
  // Handle file selection for image upload
  const handleFileChange = (e) => {
    const file = e.target.files[0];
    if (!file) return;
    
    // Validate file type
    const validTypes = ['image/jpeg', 'image/png', 'image/gif', 'image/webp'];
    if (!validTypes.includes(file.type)) {
      toast.error('Please select a valid image file (JPEG, PNG, GIF, WEBP)');
      return;
    }
    
    // Validate file size (max 5MB)
    if (file.size > 5 * 1024 * 1024) {
      toast.error('Image size must be less than 5MB');
      return;
    }
    
    setImageFile(file);
    
    // Create preview
    const reader = new FileReader();
    reader.onload = (e) => {
      setImagePreview(e.target.result);
    };
    reader.readAsDataURL(file);
  };
  
  // Handle image upload to Firebase Storage
  const handleImageUpload = async () => {
    if (!imageFile) {
      toast.error('Please select an image to upload');
      return;
    }
    
    // Check if user is authenticated
    if (!currentUser) {
      toast.error('You must be logged in to upload images');
      return;
    }
    
    // Optional: Add admin role check in application code
    // Uncomment this block if you want to restrict uploads to admin users
    /*
    if (userRole !== 'admin') {
      toast.error('Only admin users can upload blog images');
      return;
    }
    */
    
    try {
      setUploadingImage(true);
      
      // Create a unique filename
      const timestamp = new Date().getTime();
      const filename = `blog-images/${timestamp}-${imageFile.name}`;
      const storageRef = ref(storage, filename);
      
      // Upload the file
      await uploadBytes(storageRef, imageFile);
      
      // Get download URL
      const downloadURL = await getDownloadURL(storageRef);
      
      // Update form data with image URL
      setFormData(prev => ({ ...prev, imageUrl: downloadURL }));
      
      // Clear file input
      setImageFile(null);
      setImagePreview(null);
      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }
      
      toast.success('Image uploaded successfully');
    } catch (error) {
      console.error('Error uploading image:', error);
      toast.error('Failed to upload image. Please try again.');
    } finally {
      setUploadingImage(false);
    }
  };
  
  // Cancel image upload
  const cancelImageUpload = () => {
    setImageFile(null);
    setImagePreview(null);
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  // Handle form input changes
  const handleChange = (e) => {
    const { name, value } = e.target;
    
    // If the title is changed, also update the slug (but only if slug is empty or auto-generated)
    if (name === 'title' && (!formData.slug || formData.slug === generateSlug(formData.title))) {
      setFormData(prev => ({ 
        ...prev, 
        [name]: value,
        slug: generateSlug(value)
      }));
    } else {
      setFormData(prev => ({ ...prev, [name]: value }));
    }
  };
  
  // Handle form submission
  const handleSubmit = async (e) => {
    e.preventDefault();
    
    try {
      setLoading(true);
      
      // Verify CSRF token - in a real application, this would be verified on the server
      const storedToken = sessionStorage.getItem('blog_editor_csrf_token');
      if (storedToken !== csrfToken) {
        toast.error('Security validation failed. Please refresh the page and try again.');
        return;
      }
      
      // Validate required fields
      if (!formData.title.trim()) {
        toast.error('Title is required');
        return;
      }
      
      if (!formData.content.trim()) {
        toast.error('Content is required');
        return;
      }
      
      // Sanitize inputs
      const sanitizeInput = (input) => {
        // Basic sanitization - more could be done based on requirements
        if (!input) return '';
        return input.trim()
          .replace(/</g, '&lt;')  // Replace < with &lt;
          .replace(/>/g, '&gt;'); // Replace > with &gt;
      };
      
      // URLs should be validated, not just sanitized
      const validateUrl = (url) => {
        if (!url || url.trim() === '') return '';
        try {
          const parsedUrl = new URL(url.trim());
          return ['http:', 'https:'].includes(parsedUrl.protocol) ? url.trim() : '';
        } catch (error) {
          console.error('Invalid URL:', error);
          toast.error('Invalid image URL format. Please provide a valid URL.');
          return '';
        }
      };
      
      // Prepare post data with sanitized inputs
      const postData = {
        title: sanitizeInput(formData.title),
        content: formData.content.trim(), // Content processed by marked + DOMPurify when rendered
        excerpt: sanitizeInput(formData.excerpt),
        imageUrl: validateUrl(formData.imageUrl),
        category: sanitizeInput(formData.category),
        status: ['draft', 'published'].includes(formData.status) ? formData.status : 'draft',
        slug: formData.slug.trim() || generateSlug(formData.title.trim()),
        updatedAt: serverTimestamp()
      };
      
      // Add publishDate only if status is published
      if (formData.status === 'published') {
        postData.publishDate = Timestamp.fromDate(new Date(formData.publishDate));
      }
      
      if (isEditing) {
        // Update existing post
        await updateDoc(doc(db, 'blog_posts', postId), postData);
        toast.success('Post updated successfully');
      } else {
        // Create new post
        const newPostRef = doc(collection(db, 'blog_posts'));
        postData.createdAt = serverTimestamp();
        await setDoc(newPostRef, postData);
        toast.success('Post created successfully');
        
        // Redirect to edit the new post
        navigate(`/blog/admin/edit/${newPostRef.id}`);
      }
      
      // Notify parent component
      if (onSaved) onSaved();
      
    } catch (err) {
      console.error('Error saving post:', err);
      toast.error('Failed to save post');
    } finally {
      setLoading(false);
    }
  };
  
  if (loadingPost) {
    return (
      <div className="flex justify-center py-12">
        <LoadingSpinner />
      </div>
    );
  }
  
  // Preview mode for content
  const renderPreview = () => {
    if (!formData.content) {
      return <p className="text-gray-500 italic">No content to preview</p>;
    }
    
    // You can use the marked library to render Markdown
    // For simplicity, we're just displaying the raw content with basic line breaks
    return (
      <div className="prose max-w-none">
        {formData.content.split('\n').map((line, i) => (
          <p key={i}>{line}</p>
        ))}
      </div>
    );
  };
  
  return (
    <div className="bg-white rounded-lg shadow-lg overflow-hidden">
      <div className="p-6 border-b border-gray-200">
        <div className="flex justify-between items-center">
          <h2 className="text-xl font-bold text-gray-900">
            {isEditing ? 'Edit Blog Post' : 'Create New Blog Post'}
          </h2>
          <button
            onClick={() => navigate('/blog/admin')}
            className="text-gray-600 hover:text-blue-600 flex items-center"
          >
            <ArrowLeft className="h-4 w-4 mr-1" /> Back to Posts
          </button>
        </div>
      </div>
      
      <form onSubmit={handleSubmit} className="p-6">
        {/* Hidden CSRF token field */}
        <input type="hidden" name="csrfToken" value={csrfToken} />
        {/* Title */}
        <div className="mb-6">
          <label htmlFor="title" className="block text-sm font-medium text-gray-700 mb-1">
            Title <span className="text-red-500">*</span>
          </label>
          <input
            type="text"
            id="title"
            name="title"
            value={formData.title}
            onChange={handleChange}
            required
            className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
            placeholder="Enter post title"
          />
        </div>
        
        {/* Excerpt */}
        <div className="mb-6">
          <label htmlFor="excerpt" className="block text-sm font-medium text-gray-700 mb-1">
            Excerpt
          </label>
          <textarea
            id="excerpt"
            name="excerpt"
            value={formData.excerpt}
            onChange={handleChange}
            rows="2"
            className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 resize-y"
            placeholder="Brief summary of the post (used for previews)"
          ></textarea>
        </div>
        
        {/* Content */}
        <div className="mb-6">
          <div className="flex justify-between items-center mb-1">
            <label htmlFor="content" className="block text-sm font-medium text-gray-700">
              Content <span className="text-red-500">*</span>
            </label>
            <button
              type="button"
              onClick={() => setPreviewMode(!previewMode)}
              className="text-sm text-blue-600 hover:text-blue-800 flex items-center"
            >
              {previewMode ? 'Edit' : 'Preview'} <Eye className="ml-1 h-4 w-4" />
            </button>
          </div>
          
          {previewMode ? (
            <div className="border border-gray-300 rounded-md p-4 min-h-[300px] bg-gray-50">
              {renderPreview()}
            </div>
          ) : (
            <div className="relative">
              <textarea
                id="content"
                name="content"
                value={formData.content}
                onChange={handleChange}
                required
                rows="12"
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 resize-y font-mono"
                placeholder="Write your post content here (Markdown is supported)"
              ></textarea>
              <button
                type="button"
                onClick={() => setFormData(prev => ({ ...prev, content: sampleMarkdown }))}
                className="absolute top-2 right-2 text-xs bg-gray-100 hover:bg-gray-200 text-gray-700 px-2 py-1 rounded"
              >
                Insert Sample Markdown
              </button>
            </div>
          )}
          <p className="mt-1 text-xs text-gray-500">
            Markdown formatting is supported.
          </p>
        </div>
        
        {/* Image Upload & URL */}
        <div className="mb-6">
          <label htmlFor="imageUrl" className="block text-sm font-medium text-gray-700 mb-1">
            Featured Image
          </label>
          
          {/* Image Preview & URL Field */}
          <div className="mb-3">
            <div className="flex flex-col md:flex-row gap-4">
              <div className="w-full md:w-2/3">
                {/* URL Input */}
                <input
                  type="url"
                  id="imageUrl"
                  name="imageUrl"
                  value={formData.imageUrl}
                  onChange={handleChange}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                  placeholder="https://example.com/image.jpg"
                />
                <div className="flex justify-between">
                  <p className="mt-1 text-xs text-gray-500">
                    Enter a valid image URL or upload an image
                  </p>
                  <button
                    type="button"
                    onClick={() => {
                      const placeholderUrl = generatePlaceholderImage(formData.title);
                      setFormData(prev => ({ ...prev, imageUrl: placeholderUrl }));
                    }}
                    className="text-xs text-blue-600 hover:text-blue-800"
                  >
                    Use placeholder image
                  </button>
                </div>
              </div>
              
              {/* Image Preview */}
              <div className="w-full md:w-1/3 bg-gray-100 rounded-md overflow-hidden h-32 flex items-center justify-center">
                {formData.imageUrl ? (
                  <img 
                    src={formData.imageUrl} 
                    alt="Featured" 
                    className="w-full h-full object-cover" 
                  />
                ) : (
                  <span className="text-gray-400 flex items-center">
                    <Image className="w-6 h-6 mr-2" /> No image
                  </span>
                )}
              </div>
            </div>
          </div>
          
          {/* Upload Section */}
          <div className="mt-4 border border-gray-200 rounded-md p-4 bg-gray-50">
            <h4 className="text-sm font-medium text-gray-700 mb-2">Upload New Image</h4>
            
            {/* File Input */}
            <div className="flex flex-col md:flex-row gap-3">
              <div className="flex-grow">
                <input
                  type="file"
                  id="imageUpload"
                  ref={fileInputRef}
                  onChange={handleFileChange}
                  accept="image/jpeg, image/png, image/gif, image/webp"
                  className="w-full text-sm text-gray-500 
                    file:mr-4 file:py-2 file:px-4
                    file:rounded-md file:border-0
                    file:text-sm file:font-semibold
                    file:bg-blue-50 file:text-blue-700
                    hover:file:bg-blue-100"
                />
              </div>
              <div className="flex gap-2">
                <button
                  type="button"
                  onClick={handleImageUpload}
                  disabled={!imageFile || uploadingImage}
                  className={`px-4 py-2 rounded-md text-sm font-medium flex items-center gap-2 
                    ${!imageFile || uploadingImage 
                      ? 'bg-gray-200 text-gray-500 cursor-not-allowed' 
                      : 'bg-blue-600 text-white hover:bg-blue-700'}`}
                >
                  {uploadingImage ? (
                    <>
                      <LoadingSpinner /> Uploading...
                    </>
                  ) : (
                    <>
                      <Upload className="w-4 h-4" /> Upload
                    </>
                  )}
                </button>
                {imageFile && (
                  <button
                    type="button"
                    onClick={cancelImageUpload}
                    disabled={uploadingImage}
                    className="px-4 py-2 rounded-md text-sm font-medium bg-gray-200 text-gray-700 hover:bg-gray-300 flex items-center gap-2"
                  >
                    <X className="w-4 h-4" /> Cancel
                  </button>
                )}
              </div>
            </div>
            
            {/* Image Preview */}
            {imagePreview && (
              <div className="mt-3">
                <div className="text-sm font-medium text-gray-700 mb-1">Preview:</div>
                <div className="w-full h-40 bg-gray-100 rounded-md overflow-hidden">
                  <img 
                    src={imagePreview} 
                    alt="Upload preview" 
                    className="w-full h-full object-contain" 
                  />
                </div>
              </div>
            )}
          </div>
        </div>
        
        {/* URL Slug */}
        <div className="mb-6">
          <label htmlFor="slug" className="block text-sm font-medium text-gray-700 mb-1">
            URL Slug
          </label>
          <div className="flex items-center">
            <span className="text-gray-500 mr-2">/blog/</span>
            <input
              type="text"
              id="slug"
              name="slug"
              value={formData.slug}
              onChange={handleChange}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
              placeholder="url-friendly-post-title"
            />
          </div>
          <p className="mt-1 text-xs text-gray-500">
            This will be used in the URL. Generated automatically from title if left blank.
          </p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-8">
          {/* Category */}
          <div>
            <label htmlFor="category" className="block text-sm font-medium text-gray-700 mb-1">
              Category
            </label>
            <input
              type="text"
              id="category"
              name="category"
              value={formData.category}
              onChange={handleChange}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
              placeholder="E.g., Career Tips"
            />
          </div>
          
          {/* Status */}
          <div>
            <label htmlFor="status" className="block text-sm font-medium text-gray-700 mb-1">
              Status
            </label>
            <select
              id="status"
              name="status"
              value={formData.status}
              onChange={handleChange}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
            >
              <option value="draft">Draft</option>
              <option value="published">Published</option>
            </select>
          </div>
          
          {/* Publish Date (only relevant if status is published) */}
          <div>
            <label htmlFor="publishDate" className="block text-sm font-medium text-gray-700 mb-1">
              Publish Date
            </label>
            <input
              type="date"
              id="publishDate"
              name="publishDate"
              value={formData.publishDate}
              onChange={handleChange}
              className={`w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 ${
                formData.status !== 'published' ? 'opacity-50' : ''
              }`}
              disabled={formData.status !== 'published'}
            />
          </div>
        </div>
        
        {/* Submit Button */}
        <div className="flex justify-end">
          <button
            type="submit"
            disabled={loading}
            className="inline-flex items-center px-6 py-3 bg-blue-600 text-white font-medium rounded-md hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 disabled:opacity-50"
          >
            {loading ? (
              <>
                <span className="mr-2">Saving...</span>
                <LoadingSpinner />
              </>
            ) : (
              <>
                <Save className="mr-2 h-5 w-5" />
                {isEditing ? 'Update Post' : 'Create Post'}
              </>
            )}
          </button>
        </div>
      </form>
    </div>
  );
}

export default BlogPostEditor;